import { Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map } from 'rxjs';
import { AuthenticationService } from './authentication.service';

type AccessContext = 'Pending' | "Dashboard"
type PermissionContext = 'RequestBreakfast' | 'RequestLunch' | 'RequestDinner' | 'Cancel' | "Create" | "Update" | "View" | "DebugMode";
type FlagKey = `canAccess${AccessContext}Employees` | `can${PermissionContext}Employees` | `can${PermissionContext}Meal` | `canAccess${AccessContext}Orders` | 'canAccessDashboard';
type ResourcePath = 'request-meal' | 'order-list' | 'manage-employees' | 'dashboard';

type FlagSignals = {
  [K in FlagKey]: Signal<boolean>;
};



interface Permission {
  resourceId: number;
  resourceName: string;
  resourceType: string;
  action: string;
  resourcePath: ResourcePath;
  ability: 'can' | 'cannot';
  context: string;
  type: 'full' | 'context'; // <- Add this
}


@Injectable({
  providedIn: 'root',
})
export class PermissionFlagsService {

  private permissionConfig: Record<ResourcePath, { flagToContextMap: Partial<Record<FlagKey, AccessContext | PermissionContext>> }> = {
    'dashboard': {
      flagToContextMap: {
        canAccessDashboard: 'Dashboard',
      }
    },
    'order-list': {
      flagToContextMap: {
        canAccessPendingOrders: 'Pending',
      }
    },
    'manage-employees': {
      flagToContextMap: {
        canCreateEmployees: 'Create',
        canUpdateEmployees: 'Update',
        canViewEmployees: 'View',
      }
    },
    'request-meal': {
      flagToContextMap: {
        canRequestBreakfastMeal: 'RequestBreakfast',
        canRequestLunchMeal: 'RequestLunch',
        canRequestDinnerMeal: 'RequestDinner',
        canCancelMeal: 'Cancel',
        canDebugModeMeal: 'DebugMode',
      }
    }
  };

  private permissionsSubject = new BehaviorSubject<Permission[]>([]);
  permissions$ = this.permissionsSubject.asObservable();

  readonly flags: FlagSignals = Object.fromEntries(
    Object.entries(this.permissionConfig)
      .flatMap(([_resourcePath, config]) =>
        Object.entries(config.flagToContextMap).map(([flag, _]) => [
          flag,
          toSignal(
            this.permissions$.pipe(
              map((permissions) => this.calculateFlagValue(flag as FlagKey, permissions))
            ),
            { initialValue: false }
          )
        ])
      )
  ) as FlagSignals;

  constructor(private auth: AuthenticationService) {
    this.loadAndUpdatePermissions();
  }

  private calculateFlagValue(flag: FlagKey, permissions: Permission[]): boolean {
    if (permissions.length === 0) return false;

    // Flags that should NEVER be granted by 'full' type — only explicit context
    const contextOnlyFlags: FlagKey[] = ['canDebugModeMeal'];

    for (const permission of permissions) {
      const config = this.permissionConfig[permission.resourcePath];
      if (!config) continue;

      const contextForFlag = config.flagToContextMap[flag];
      if (!contextForFlag) continue;

      if (permission.ability !== 'can') {
        return false;
      }

      if (permission.type === 'full') {
        // If this flag requires explicit context, don't grant it via 'full'
        if (contextOnlyFlags.includes(flag)) {
          // Check if the context string explicitly includes this flag's context
          if (permission.context) {
            const allowedContexts = permission.context.split(',').map(c => c.trim());
            if (allowedContexts.includes(contextForFlag)) {
              return true;
            }
          }
          // Otherwise skip — don't grant debug via full
          continue;
        }
        return true;
      }

      if (permission.type === 'context') {
        const allowedContexts = permission.context.split(',').map(c => c.trim());
        if (allowedContexts.includes(contextForFlag)) {
          return true;
        }
      }
    }

    return false;
  }

  loadAndUpdatePermissions(): void {
    const userInfoStr = sessionStorage.getItem(`${this.auth.APP_NAMESPACE}_user_info`);
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      if (userInfo?.permissions) {
        const permissions: Permission[] = userInfo.permissions;
        this.permissionsSubject.next(permissions);
      }
    }
  }

  refreshPermissions(): void {
    this.loadAndUpdatePermissions();
  }
}
